import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    CircularProgress,
    TextField,
    Link,
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { DataGrid, GridColDef} from "@mui/x-data-grid";
import Layout from "../components/Layout";
import axiosExt from '../axios';
import { API_BASE_URL } from "../config.ts";
import { Email } from "../types";
import { useTranslation } from "react-i18next";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";


const EmailListPage: React.FC = () => {
    const { t } = useTranslation();

    const [emails, setEmails] = useState<Email[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false); 
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState < GridFilterModel>({ items: [] });

    const emailFilterStorageKey = "emailFilters";
    const [fromFilter, setFromFilter] = useState("");
    const [subjectFilter, setSubjectFilter] = useState("");
    const [receivedStart, setReceivedStart] = useState<Dayjs | null>(null);
    const [receivedEnd, setReceivedEnd] = useState<Dayjs | null>(null);
    const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);


    const savedSortKey = "sortEmailList";
    const savedFilterKey = "filterEmailList";

    const hasActiveFilters = () => {
        const hasFrom = fromFilter.trim().length >= 3;
        const hasSubject = subjectFilter.trim().length >= 3;

        const hasBothDates =
            receivedStart !== null &&
            receivedEnd !== null;

        // If both dates exist → enforce max 31 days
        let validDateRange = false;
        if (hasBothDates) {
            const diff = receivedEnd.diff(receivedStart, "day");
            validDateRange = diff >= 0 && diff <= 31;
        }

        return hasFrom || hasSubject || validDateRange;
    };


    useEffect(() => {
        const stored = localStorage.getItem(emailFilterStorageKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.from) setFromFilter(parsed.from);
                if (parsed.subject) setSubjectFilter(parsed.subject);
                if (parsed.receivedStart) setReceivedStart(dayjs(parsed.receivedStart));
                if (parsed.receivedEnd) setReceivedEnd(dayjs(parsed.receivedEnd));
            } catch (e) {
                console.warn("Failed to parse saved email filters:", e);
            }
        } else {
            // No filters? Default to last 30 days
            const now = dayjs();
            setReceivedStart(now.subtract(30, 'day').startOf('day'));
            setReceivedEnd(now.endOf('day'));
        }

        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);


        setIsFiltersInitialized(true);
    }, []);

    useEffect(() => {
        if (!isFiltersInitialized) return;

        //// Only fetch if at least one filter is active
        //const hasActiveFilters = () => {
        //    const hasFrom = !!fromFilter.trim();
        //    const hasSubject = subjectFilter.trim().length >= 3;
        //    const hasBothDates = receivedStart !== null && receivedEnd !== null;

        //    return hasFrom || hasSubject || hasBothDates;
        //};

        if (hasActiveFilters()) {
            fetchEmails();
        }
    }, [isFiltersInitialized, fromFilter, subjectFilter, receivedStart, receivedEnd]);

    const fetchEmails = async () => {
        setLoading(true);
        try {
            // Save filters to local storage
            localStorage.setItem(emailFilterStorageKey, JSON.stringify({
                from: fromFilter,
                subject: subjectFilter,
                receivedStart: receivedStart?.toISOString() ?? null,
                receivedEnd: receivedEnd?.toISOString() ?? null,
            }));


            const token = localStorage.getItem("token");

            const params: {
                includeContent: boolean;
                from?: string;
                subject?: string;
                receivedAfter?: string;
                receivedBefore?: string;
            } = {
                includeContent: false,
            };

            if (fromFilter) params.from = fromFilter;
            if (subjectFilter) params.subject = subjectFilter;
            if (receivedStart)
                params.receivedAfter = receivedStart.toISOString();
            if (receivedEnd)
                params.receivedBefore = receivedEnd.add(1, 'day').startOf('day').toDate().toISOString();

            const response = await axiosExt.get(`${API_BASE_URL}/Emails?includeContent=false`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });


            setEmails(response.data);
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error("Caught an error:", error.message);
            }
            else {
                console.error("An unknown error occurred");
            }
           
        } finally {
            setLoading(false);
        }
    };

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    /*const handleView = (email: Email) => {
        setSelectedEmail(email);
        setOpen(true);
    };*/

    const handleView = async (email: Email) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get(`${API_BASE_URL}/Emails/${email.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSelectedEmail(response.data);
            setOpen(true);
        } catch (error) {
            console.error("Error fetching email details:", error);
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = async (id: string) => {
        //const token = localStorage.getItem("token");

        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
            /*await axiosExt.delete(`${API_BASE_URL}/emails/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setEmails(emails.filter((c) => c.id !== id));*/
        } catch (error) {
            console.error("Error deleting email:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/emails/${itemToDelete}`, {
                 headers: {
                     Authorization: `Bearer ${token}`,
                 },
             });
            setEmails(emails.filter((c) => c.id !== itemToDelete));
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedEmail(null);
    };
      
    const handleDownloadAttachment = async (fileId: string, downloadAsFileName: string) => {
        try {
            const token = localStorage.getItem("token");

            // Step 1: Get the file URL from your backend
            const response = await axiosExt.get(`${API_BASE_URL}/emails/download/${fileId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const fileUrl = response.data.fileUrl;

            // Step 2: Download file as blob from Azure Blob Storage
            const fileResponse = await axiosExt.get(fileUrl, { responseType: 'blob' });

            const blob = new Blob([fileResponse.data]);
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = downloadAsFileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading the file:", error);
            alert("Failed to download the file.");
        }
    };


    const emailColumns: GridColDef[] = [
        {
            field: "incoming", headerName: t("Incoming"), width: 150,
            renderCell: (params) => (params.value ? t("yes") : t("no")),
        },
        { field: "from", headerName: t("From"), width: 150 },
        { field: "to", headerName: t("To"), width: 150 },
        { field: "cc", headerName: t("CC"), width: 150 },
        { field: "subject", headerName: t("Subject"), width: 200 },
        {
            field: "receivedAt",
            headerName: t("Received At"),
            width: 180,
            renderCell: (params) => {
                if (!params.value) return ""; // Handle empty values

                const utcDate = new Date(params.value + "Z");
                // Convert UTC to the user's local time zone
                const localDate = new Intl.DateTimeFormat(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Browser's time zone
                }).format(utcDate);
                return localDate;
            }
        },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 340,
            renderCell: (params) => {
                const email = params.row as Email;

                return (
                    <>
                        <Button
                            variant="contained"
                            onClick={() => handleView(email)}
                            style={{ marginRight: "10px" }}
                        >
                            {t("View")}
                        </Button>

                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleDelete(email?.id || "")}
                            style={{ marginRight: "10px" }}
                        >
                            {t("Delete")}
                        </Button>

                        {email.orderId && ( // Only show if orderId exists
                            <Button
                                variant="contained"
                                href={`/OrderDetailsPage/${email.orderId}`}
                                target="_self" 
                            >
                                {t("Order")}
                            </Button>
                        )}
                    </>
                );
            }

        },
    ];

    return (
        <Layout>
            <div>
                <div style={{ width: "100%", display: "flex" }}>
                    <h2>{t("Email List")}</h2>
                </div>
            </div>

            {loading ? (
                <CircularProgress />
            ) : (
                    <Box sx={{ height: "100%", width: "100%" }}>
                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                            <TextField
                                label="From"
                                value={fromFilter}
                                onChange={(e) => setFromFilter(e.target.value)}
                                variant="outlined"
                                size="small"
                            />
                            <TextField
                                label="Subject"
                                value={subjectFilter}
                                onChange={(e) => setSubjectFilter(e.target.value)}
                                variant="outlined"
                                size="small"
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Received Start"
                                    value={receivedStart}
                                    onChange={(newValue) => setReceivedStart(newValue)}
                                />
                                <DatePicker
                                    label="Received End"
                                    value={receivedEnd}
                                    onChange={(newValue) => setReceivedEnd(newValue)}
                                />
                            </LocalizationProvider>

                            <Button
                                variant="contained"
                                onClick={() => {
                                    if (!hasActiveFilters()) {
                                        alert("Enter From (min 3 chars), Subject (min 3 chars), or a valid date interval (≤ 30 days).");
                                        return;
                                    }
                                    fetchEmails();
                                }}
                            >
                                Apply Filters
                            </Button>
                        </Box>
                        {!hasActiveFilters() ? (
                            <Typography sx={{ mt: 2 }} color="error">
                                Please select at least one filter to load emails. Subject filter must be at least 3 characters.
                            </Typography>
                        ) : (
                            <DataGrid
                                rows={emails}
                                columns={emailColumns}
                                sortModel={sortModel}
                                onSortModelChange={handleSortModelChange}
                                filterModel={filterModel}
                                onFilterModelChange={handleFilterModelChange}
                                getRowId={(row: Email) => row.id}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 100, page: 0 },
                                    },
                                }}
                                    pageSizeOptions={[25, 50, 100, 200]}
                            />
                        )}
                </Box>
            )}

            {/* View Email Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>{t("Email")}</DialogTitle>
                <DialogContent dividers>
                    {selectedEmail && (
                        <>
                            <Typography variant="subtitle2">
                                {t("Received At")}: {selectedEmail.receivedAt
                                    ? new Intl.DateTimeFormat(undefined, {
                                        dateStyle: "medium",
                                        timeStyle: "short",
                                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone // Browser's timezone
                                    }).format(new Date(selectedEmail.receivedAt + "Z")) // Ensure it's treated as UTC
                                    : "N/A"}
                            </Typography>
                            <Typography variant="subtitle2">{t("From")}: {selectedEmail.from}</Typography>
                            <Typography variant="subtitle2">{t("To")}: {selectedEmail.to}</Typography>
                            <Typography variant="subtitle2">{t("New")}: {selectedEmail.isRead}</Typography>
                            {selectedEmail.cc && (
                                <>
                                    <Typography variant="subtitle2">{t("CC")}: {selectedEmail.cc}</Typography>
                                    <Typography></Typography>
                                </>
                            )}
                            {selectedEmail.bcc && (
                                <>
                                    <Typography variant="subtitle2">{t("BCC")}: {selectedEmail.bcc}</Typography>
                                    <Typography></Typography>
                                </>
                            )}

                            <Typography variant="subtitle2">{t("Subject")}: {selectedEmail.subject}</Typography>
                            <div
                                dangerouslySetInnerHTML={{ __html: selectedEmail.content ?? ""}}
                                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                            ></div>
                            

                            <Typography variant="subtitle2" sx={{ mt: 2 }}>{t("Attachments")}:</Typography>
                            {selectedEmail.attachments?.length ? (
                                selectedEmail.attachments
                                    .filter((attachment) => {
                                        const fileName = attachment.fileName;
                                        const cidPattern = new RegExp(`cid:.*${fileName}`, "i");
                                        return !cidPattern.test(selectedEmail.content || "");
                                    })
                                    .map((attachment) => (
                                        <Link
                                            key={attachment.id}
                                            onClick={() => handleDownloadAttachment(attachment.id || "", attachment.fileName)}
                                            sx={{ cursor: "pointer", display: "block", mt: 1 }}
                                        >
                                            {attachment.fileName}
                                        </Link>
                                    ))
                            ) : (
                                <Typography>{t("No attachments")}</Typography>
                            )}

                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t("Close")}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                    <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default EmailListPage;
